import React, { useCallback, useState, useRef } from 'react';
import { DateRange } from '@mui/lab';
import InputRangePicker from './RangPickerInput';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '~/UIKit/Style';
import { Button } from '~/UIKit';
import { useMediaQuery, Theme } from '@mui/material';
import { StaticRangePicker } from './StaticRangePicker';
import { PopoverToModal } from '~/Components';

const useStyles = makeStyles(() => ({
  footer: {
    padding: '15px 25px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));

interface IDateRangePickerProps {
  onDateChange: Function;
  defaultValue?: any;
  showClear?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
}

export function DateRangePicker({
  onDateChange,
  defaultValue = [null, null],
  showClear = false,
  disableFuture = false,
  disablePast = false,
}: IDateRangePickerProps) {
  const [value, setValue] = useState<DateRange<Date>>(defaultValue);
  const [updatedValue, setUpdated] = useState<DateRange<Date>>(defaultValue);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { classes } = useStyles();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const inputRef = useRef(null);

  const handleOnDateChange = useCallback(
    (accept = false) => {
      if (accept) {
        setUpdated(value);
        onDateChange(value);
      } else {
        setValue(updatedValue);
      }
      setOpen(false);
    },
    [updatedValue, value],
  );

  const onClear = useCallback(() => {
    setValue(defaultValue);
    onDateChange(defaultValue);
    setUpdated(defaultValue);
  }, []);

  const rangePickerComponent = (
    <>
      <StaticRangePicker
        handleDateChange={setValue}
        open={open}
        value={value}
        isMobile={isMobile}
        disableFuture={disableFuture}
        disablePast={disablePast}
      />
      {!isMobile && (
        <div className={classes.footer}>
          <Button size="small" color="primary" onClick={() => onClear()} variant="text">
            {t('app.rangepicker.reset')}
          </Button>
          <Button
            size="small"
            className={classes.applyBtn}
            color="primary"
            onClick={() => handleOnDateChange(true)}
            variant="contained"
          >
            {t('app.rangepicker.apply')}
          </Button>
        </div>
      )}
    </>
  );

  return (
    <>
      <InputRangePicker
        startProps={{ inputProps: { value: updatedValue?.[0], ref: inputRef }, ref: inputRef }}
        endProps={{ inputProps: { value: updatedValue?.[1] } }}
        handleClearDate={onClear}
        handleOpenDatePicker={() => setOpen(true)}
        showClear={showClear}
      />
      <PopoverToModal
        onActionTaken={() => handleOnDateChange(true)}
        open={open}
        width="100%"
        anchorEl={inputRef.current}
        onClose={() => setOpen(false)}
        title={t('app.payouts.modalTitle')}
        actionName={t('app.filter.apply')}
      >
        {rangePickerComponent}
      </PopoverToModal>
    </>
  );
}
